import { SANDBOX_PRESET_TAG as T } from '../presetTags'
import { SANDBOX_PRESET_BODY_CSS, type SandboxComponentPreset } from '../../sandboxPresetTypes'

/**
 * HTML5 **Drag and Drop** (not Pointer events): must `preventDefault` on `dragover` to allow drop.
 * `afterElement` compares pointer Y to row midpoints to decide insert-before target; ghost uses `.dragging` for opacity.
 */
const shell = SANDBOX_PRESET_BODY_CSS

const preset: SandboxComponentPreset = {
  id: 'reorder-cards',
  category: 'drag-drop',
  title: 'Reorder cards',
  description: 'HTML5 drag-and-drop to reorder vertical cards.',
  tags: [T.dnd, T.sort],
  html: `<h2>Reorder</h2>
<p class="hint">Drag by the handle (⋮⋮).</p>

<ul id="list" class="list">
  <li draggable="true" data-id="1"><span class="h" aria-hidden="true">⋮⋮</span> Alpha task</li>
  <li draggable="true" data-id="2"><span class="h">⋮⋮</span> Beta task</li>
  <li draggable="true" data-id="3"><span class="h">⋮⋮</span> Gamma task</li>
</ul>`,
  css: `${shell}

h2 {
  color: var(--nogrok-preview-primary);
  margin: 0 0 0.25rem;
}

.hint {
  font-size: 0.8rem;
  opacity: 0.85;
  margin: 0 0 0.75rem;
}

.list {
  list-style: none;
  padding: 0;
  margin: 0;
  max-width: 22rem;
}

.list li {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.65rem 0.85rem;
  border: 1px solid var(--nogrok-preview-outline);
  border-radius: 8px;
  background: var(--nogrok-preview-surface-container);
  margin-bottom: 8px;
  cursor: grab;
  user-select: none;
}

.list li.dragging {
  opacity: 0.45;
}

.h {
  font-size: 0.7rem;
  opacity: 0.5;
  letter-spacing: -2px;
}`,
  js: `const list = document.getElementById('list')
let dragEl = null

list?.addEventListener('dragstart', (e) => {
  const t = e.target
  const li = t && typeof t.closest === 'function' ? t.closest('li') : null
  if (!li) return
  dragEl = li
  li.classList.add('dragging')
  e.dataTransfer?.setData('text/plain', li.dataset.id || '')
  e.dataTransfer.effectAllowed = 'move'
})

list?.addEventListener('dragend', () => {
  list?.querySelectorAll('.dragging').forEach((el) => el.classList.remove('dragging'))
  dragEl = null
})

list?.addEventListener('dragover', (e) => {
  e.preventDefault()
  if (!list || !dragEl) return
  const after = afterElement(list, e.clientY)
  if (after == null) list.appendChild(dragEl)
  else list.insertBefore(dragEl, after)
})

// Which static row should the dragged item be inserted *before*? Compare pointer Y to row centers.
function afterElement(ul, y) {
  const els = [...ul.querySelectorAll('li:not(.dragging)')]
  return els.reduce(
    (close, child) => {
      const box = child.getBoundingClientRect()
      const off = y - box.top - box.height / 2
      if (off < 0 && off > close.offset) return { offset: off, el: child }
      return close
    },
    { offset: Number.NEGATIVE_INFINITY, el: null },
  ).el
}`,
}

export default preset
